// LLM Models Configuration for OpenRouter API

/**
 * Available LLM models with their configurations
 */
export const LLM_MODELS = {
  STANDARD: {
    id: 'google/gemini-2.5-flash-lite-preview-06-17',
    name: 'Gemini 2.5 Flash Lite',
    description: 'Lightweight reasoning model optimized for ultra-low latency and cost efficiency',
    maxTokens: 20000,
    contextLength: 1048576,
    // Search-specific token configurations
    searchTokens: {
      web: 1000,     // Minimal 1000 token untuk web search
      academic: 1500 // Minimal 1500 token untuk academic search
    },
    pricing: {
      input: 0.10, // per million tokens
      output: 0.40 // per million tokens
    },
    features: {
      reasoning: false, // Thinking disabled by default for speed
      multimodal: true,
      streaming: true
    }
  },
  THINKING: {
    id: 'google/gemini-2.5-flash',
    name: 'Gemini 2.5 Flash',
    description: 'Advanced reasoning model with built-in thinking capabilities',
    maxTokens: 12000,
    contextLength: 1048576,
    // Search-specific token configurations
    searchTokens: {
      web: 1000,     // Minimal 1000 token untuk web search
      academic: 1500 // Minimal 1500 token untuk academic search
    },
    reasoning: {
      enabled: true,
      maxTokens: 1000
    },
    pricing: {
      input: 0.30, // per million tokens
      output: 2.50, // per million tokens
      images: 1.238 // per 1K input images
    },
    features: {
      reasoning: true,
      multimodal: true,
      streaming: true,
      thinking: true
    }
  },
  GENIUS: {
    id: 'google/gemini-2.5-flash',
    name: 'Gemini 2.5 Flash (Genius)',
    description: 'Advanced genius-level model for comprehensive academic writing and research',
    maxTokens: 20000,
    contextLength: 1048576,
    // Search-specific token configurations optimized for academic work
    searchTokens: {
      web: 2000,     // Higher token untuk web search yang komprehensif
      academic: 3000 // Higher token untuk academic search yang mendalam
    },
    reasoning: {
      enabled: true,
      maxTokens: 2000
    },
    pricing: {
      input: 0.30, // per million tokens
      output: 2.50, // per million tokens
      images: 1.238 // per 1K input images
    },
    features: {
      reasoning: true,
      multimodal: true,
      streaming: true,
      thinking: true,
      academicWriting: true,
      longFormContent: true,
      citationGeneration: true
    }
  },
  CLEVER: {
    id: 'x-ai/grok-3-mini',
    name: 'Grok 3 Mini',
    description: 'Lightweight model that thinks before responding. Fast, smart, and great for logic-based tasks',
    maxTokens: 20000,
    contextLength: 131072,
    // Search-specific token configurations
    searchTokens: {
      web: 1200,     // Optimal token untuk web search
      academic: 1800 // Optimal token untuk academic search
    },
    reasoning: {
      enabled: true,
      effort: 'medium'
    },
    pricing: {
      input: 0.30, // per million tokens
      output: 0.50 // per million tokens
    },
    features: {
      reasoning: true,
      multimodal: false,
      streaming: true,
      thinking: true,
      logicBased: true
    }
  },
  AGENTPRO: {
    id: 'moonshotai/kimi-k2',
    name: 'Kimi K2 (AgentPro)',
    description: 'Advanced AI model specialized for creating custom agents based on user requirements',
    maxTokens: 30000,
    contextLength: 200000,
    // Search-specific token configurations for agent creation
    searchTokens: {
      web: 2500,     // Higher token untuk comprehensive agent analysis
      academic: 3500 // Higher token untuk academic agent creation
    },
    reasoning: {
      enabled: true,
      maxTokens: 3000
    },
    pricing: {
      input: 0.50, // per million tokens
      output: 1.50 // per million tokens
    },
    features: {
      reasoning: true,
      multimodal: true,
      streaming: true,
      thinking: true,
      agentCreation: true,
      customization: true,
      integrationPlanning: true
    }
  },
  SMART: {
    id: 'google/gemini-2.5-flash',
    name: 'Gemini 2.5 Flash (Smart)',
    description: 'Smart model with advanced capabilities but without thinking process for faster responses',
    maxTokens: 200000,
    contextLength: 1048576,
    // Search-specific token configurations
    searchTokens: {
      web: 1500,     // Optimal token untuk web search
      academic: 2000 // Optimal token untuk academic search
    },
    pricing: {
      input: 0.30, // per million tokens
      output: 2.50, // per million tokens
      images: 1.238 // per 1K input images
    },
    features: {
      reasoning: false, // Thinking disabled for speed
      multimodal: true,
      streaming: true,
      thinking: false
    }
  },
  WRITE: {
    id: 'google/gemini-2.5-flash',
    name: 'Gemini 2.5 Flash (Write)',
    description: 'Advanced writing model optimized for long-form content creation with extended token capacity',
    maxTokens: 100000,
    contextLength: 1048576,
    // Search-specific token configurations optimized for writing
    searchTokens: {
      web: 2000,     // Higher token untuk web search yang komprehensif
      academic: 3000 // Higher token untuk academic search yang mendalam
    },
    reasoning: {
      enabled: true,
      maxTokens: 2000
    },
    pricing: {
      input: 0.30, // per million tokens
      output: 2.50, // per million tokens
      images: 1.238 // per 1K input images
    },
    features: {
      reasoning: true,
      multimodal: true,
      streaming: true,
      thinking: true,
      longFormWriting: true,
      extendedContent: true,
      creativeWriting: true
    }
  }
};

// Backward compatibility alias (deprecated - use AGENTPRO instead)
LLM_MODELS.BOTPRO = LLM_MODELS.AGENTPRO;

/**
 * Model modes for UI selection
 */
export const MODEL_MODES = {
  STANDARD: 'standard',
  THINKING: 'thinking',
  GENIUS: 'genius',
  CLEVER: 'clever',
  AGENTPRO: 'agentpro',
  SMART: 'smart',
  WRITE: 'write',
  // Backward compatibility alias (deprecated - use AGENTPRO instead)
  BOTPRO: 'agentpro'
};

/**
 * Get model configuration by mode
 * @param {string} mode - The model mode (standard or thinking)
 * @param {string} searchType - The search type (web or academic) for token optimization
 * @returns {Object} Model configuration
 */
export const getModelConfig = (mode, searchType = 'web') => {
  let baseConfig;
  switch (mode) {
    case MODEL_MODES.STANDARD:
      baseConfig = LLM_MODELS.STANDARD;
      break;
    case MODEL_MODES.THINKING:
      baseConfig = LLM_MODELS.THINKING;
      break;
    case MODEL_MODES.GENIUS:
      baseConfig = LLM_MODELS.GENIUS;
      break;
    case MODEL_MODES.CLEVER:
      baseConfig = LLM_MODELS.CLEVER;
      break;
    case MODEL_MODES.AGENTPRO:
     case MODEL_MODES.BOTPRO: // Backward compatibility
       baseConfig = LLM_MODELS.AGENTPRO;
       break;
    case MODEL_MODES.SMART:
      baseConfig = LLM_MODELS.SMART;
      break;
    case MODEL_MODES.WRITE:
      baseConfig = LLM_MODELS.WRITE;
      break;

    default:
      baseConfig = LLM_MODELS.STANDARD;
  }
  
  // Override maxTokens based on search type if searchTokens configuration exists
  if (baseConfig.searchTokens && baseConfig.searchTokens[searchType]) {
    return {
      ...baseConfig,
      maxTokens: Math.max(baseConfig.searchTokens[searchType], baseConfig.maxTokens)
    };
  }
  
  return baseConfig;
};

/**
 * Get minimum tokens required for search type
 * @param {string} searchType - The search type (web or academic)
 * @returns {number} Minimum tokens required
 */
export const getMinTokensForSearchType = (searchType) => {
  const standardConfig = LLM_MODELS.STANDARD;
  if (standardConfig.searchTokens && standardConfig.searchTokens[searchType]) {
    return standardConfig.searchTokens[searchType];
  }
  return 1000; // Default fallback
};

/**
 * Get available model options for UI
 * @returns {Array} Array of model options
 */
export const getModelOptions = () => [
  {
    value: MODEL_MODES.STANDARD,
    label: 'Standard Mode',
    description: 'Fast and efficient for general tasks (Web: 1000+ tokens, Academic: 1500+ tokens)',
    model: LLM_MODELS.STANDARD.name
  },
  {
    value: MODEL_MODES.THINKING,
    label: 'Thinking Mode',
    description: 'Advanced reasoning with thinking process (Web: 1000+ tokens, Academic: 1500+ tokens)',
    model: LLM_MODELS.THINKING.name
  },
  {
    value: MODEL_MODES.CLEVER,
    label: 'Clever Mode',
    description: 'Logic-based reasoning with medium effort thinking (Web: 1200+ tokens, Academic: 1800+ tokens)',
    model: LLM_MODELS.CLEVER.name
  },
  {
    value: MODEL_MODES.SMART,
    label: 'Smart Mode',
    description: 'Advanced capabilities without thinking process for faster responses (Web: 1500+ tokens, Academic: 2000+ tokens)',
    model: LLM_MODELS.SMART.name
  }
];